import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Accordion, Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  getMicrosoftSetupAPI,
  grantConsentAPI,
  markTeamsAppUploadedAPI,
  runBookingTestAPI,
  runMicrosoftConnectivityTestAPI,
  saveMicrosoftCredentialsAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { companyUrl } from '../../../utils/mhNav';
import styles from './MicrosoftSetup.module.scss';

const STEPS = ['Credentials', 'Consent', 'Teams App', 'Test Booking'];

export default function MicrosoftSetup() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [showSecret, setShowSecret] = useState(false);
  const [creds, setCreds] = useState({ tenantId: '', clientId: '', clientSecret: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMicrosoftSetupAPI();
      setState(res.data);
      setCreds((c) => ({ ...c, ...res.data.credentials }));
      setStep(res.data.currentStep || 1);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCreds = async () => {
    try {
      await saveMicrosoftCredentialsAPI(creds);
      toast.success('Saved — continue to consent');
      setStep(2);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const verifyConsent = async () => {
    try {
      await grantConsentAPI();
      toast.success('Consent verified');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const grant = () => {
    window.open('https://login.microsoftonline.com/adminconsent', '_blank', 'noopener,noreferrer');
    toast.info('Complete consent in the new tab (mock)');
  };

  const downloadManifest = () => {
    const blob = new Blob([JSON.stringify({ name: 'MeetingHub Teams App', version: '1.0.0' }, null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'teams-manifest.json';
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Manifest downloaded');
  };

  const runTest = async () => {
    try {
      await runBookingTestAPI();
      toast.success('Booking test finished');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const runAll = async () => {
    try {
      await runMicrosoftConnectivityTestAPI();
      toast.success('Quick connectivity check OK');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading && !state) {
    return (
      <div>
        <PageHeader title="Microsoft" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations', path: companyUrl('integrations') }, { label: 'Microsoft' }]} />
        <LoadingSkeleton rows={6} cols={2} />
      </div>
    );
  }

  if (error && !state) {
    return (
      <div>
        <PageHeader title="Microsoft" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations', path: companyUrl('integrations') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  const missingScopes = (state.consent?.scopes || []).filter((s) => !s.granted);

  return (
    <div>
      <PageHeader
        title="Microsoft 365 setup"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Integrations', path: companyUrl('integrations') },
          { label: 'Microsoft' },
        ]}
        secondaryActions={[{ label: 'Run connectivity test', onClick: runAll, variant: 'outline-primary' }]}
      />

      <div className={styles.stepper}>
        {STEPS.map((label, i) => {
          const n = i + 1;
          const cls = [styles.step];
          if (n < step) cls.push(styles.stepDone);
          else if (n === step) cls.push(styles.stepCurrent);
          else cls.push(styles.stepUpcoming);
          return (
            <button key={label} type="button" className={cls.join(' ')} onClick={() => setStep(n)}>
              {n}. {label}
            </button>
          );
        })}
      </div>

      {step === 1 && (
        <Card>
          <Card.Body>
            <h2 className="h6">Step 1 — Credentials</h2>
            <Form.Group className="mb-2">
              <Form.Label>Tenant ID</Form.Label>
              <Form.Control value={creds.tenantId} onChange={(e) => setCreds((s) => ({ ...s, tenantId: e.target.value }))} />
              <Form.Text>Find this in Azure Portal → Overview</Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Client ID</Form.Label>
              <Form.Control value={creds.clientId} onChange={(e) => setCreds((s) => ({ ...s, clientId: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Client secret</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type={showSecret ? 'text' : 'password'} value={creds.clientSecret} onChange={(e) => setCreds((s) => ({ ...s, clientSecret: e.target.value }))} />
                <Button variant="outline-secondary" size="sm" onClick={() => setShowSecret((v) => !v)}>
                  {showSecret ? 'Hide' : 'Show'}
                </Button>
              </div>
            </Form.Group>
            <Accordion className="mb-3">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Where to find these values</Accordion.Header>
                <Accordion.Body className="small">Azure AD → App registrations → Certificates &amp; secrets. Copy Directory (tenant) ID and Application (client) ID.</Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Button onClick={saveCreds}>Save &amp; continue</Button>
            {state.credentials?.saved && <Alert variant="success" className="mt-3 mb-0 py-2">Credentials saved</Alert>}
          </Card.Body>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <Card.Body>
            <h2 className="h6">Step 2 — Consent</h2>
            <p>
              Graph API consent: <Badge bg={state.consent?.graphStatus === 'granted' ? 'success' : 'warning'}>{state.consent?.graphStatus}</Badge>{' '}
              <span className="text-muted small">{state.consent?.verifiedAt ? new Date(state.consent.verifiedAt).toLocaleString() : ''}</span>
            </p>
            <ul className="list-unstyled small mb-3">
              {(state.consent?.scopes || []).map((s) => (
                <li key={s.name}>
                  {s.granted ? '✓' : '✗'} {s.name}
                </li>
              ))}
            </ul>
            {missingScopes.length > 0 && (
              <Alert variant="danger" className="small">
                Missing: {missingScopes.map((s) => s.name).join(', ')}
              </Alert>
            )}
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={grant}>
                Grant consent
              </Button>
              <Button variant="outline-primary" onClick={verifyConsent}>
                Verify consent
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <Card.Body>
            <h2 className="h6">Step 3 — Teams app</h2>
            <Button className="mb-3" variant="outline-primary" onClick={downloadManifest}>
              Download manifest
            </Button>
            <Accordion className="mb-3">
              {['Open Teams admin center', 'Upload custom app', 'Approve for org', 'Install for users', 'Verify bot ID', 'Run policy check'].map((t, i) => (
                <Accordion.Item key={t} eventKey={String(i)}>
                  <Accordion.Header>
                    Step {i + 1}: {t}
                  </Accordion.Header>
                  <Accordion.Body className="small">Follow Microsoft documentation for your tenant (mock instructions).</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
            <p>
              Upload status: <Badge bg="info">{state.teamsApp?.uploadStatus}</Badge>
            </p>
            <Button
              onClick={async () => {
                try {
                  await markTeamsAppUploadedAPI();
                  toast.success('Marked uploaded');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Mark as uploaded
            </Button>
          </Card.Body>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <Card.Body>
            <h2 className="h6">Step 4 — Test booking</h2>
            <Button className="mb-3" onClick={runTest}>
              Run booking test
            </Button>
            {state.testBooking?.lastRunAt && (
              <Alert variant={state.testBooking.success ? 'success' : 'danger'}>
                {state.testBooking.success ? '✓ ' : ''}
                {state.testBooking.message}
              </Alert>
            )}
            {state.testBooking?.success && (
              <Button variant="link" className="p-0" onClick={() => navigate(companyUrl('dashboard'))}>
                Go to dashboard
              </Button>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
